import { useMutation } from '@apollo/client'

import Alert from '@web/components/Modal/Alert'
import { useModal } from '@web/components/Modal/useModal'
import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { ClipItem } from '@web/gql/graphql'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { messages } from '@web/libs/constants'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'

const mutation = gql(`
  mutation deleteClip($videoId: String!) {
    deleteClip(videoId: $videoId) {
      ... on DeleteClip_Succeed {
        deletedId
      }
      ... on BaseError {
        message
      }
    }
  }
`)

interface Props {
  videoId: string
  nClickCodes: {
    edit: string
    delete: string
    deleteNo: string
    deleteYes: string
  }
}

const useDeleteClipModal = ({ videoId, nClickCodes }: Props) => {
  const modal = useModal(Alert, {
    label: { cancel: '취소', confirm: '삭제' },
    message: '클립을 삭제하시겠습니까?',
    callback: { confirm: () => deleteClip() },
    clickCodes: {
      confirm: nClickCodes.deleteYes,
      cancel: nClickCodes.deleteNo,
    },
  })

  const [deleteClip] = useMutation(mutation, {
    variables: {
      videoId,
    },
    onCompleted: data => {
      const { DeleteClip_Succeed } = extractByTypename(data.deleteClip)
      if (!DeleteClip_Succeed) {
        openErrorToast(messages.ERR_CLIP_DELETE)
        return
      }

      return
    },
    update(cache, { data }) {
      const { DeleteClip_Succeed } = extractByTypename(data?.deleteClip)
      if (!DeleteClip_Succeed) return

      cache.modify({
        fields: {
          userClips(existing, { readField }) {
            return (existing as ClipItem[]).filter(clip => readField('id', clip) !== videoId)
          },
          userClipsCount(existing) {
            return existing - 1
          },
        },
      })
    },
    onError: error => {
      neloLogger('error', `DeleteClip NetworkError : ${extractErrorMessage(error)}`)
      openErrorToast(messages.ERR_CLIP_DELETE)
    },
  })

  return modal
}

export default useDeleteClipModal
